/**
 * AusstellungProjektPage
 * Detail view for one Kunstprojekt inside Ausstellungen.
 * Renders the project description plus a grid of all attached images.
 */

import { useParams, useNavigate } from 'react-router-dom'
import { useArtworks } from '@/hooks/useArtworks'
import { mediaUrl } from '@/utils/mediaUrl'
import { Markdown } from '@/components/ui/Markdown'
import { Seo } from '@/components/Seo'
import { useT, STRINGS, useLanguage, useFieldLang } from '@/context/LanguageContext'
import './ContentPage.css'

export function AusstellungProjektPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const { getSection, loading } = useArtworks()
  const section = getSection('ausstellungen')
  const project = section?.projects?.find((p) => p.slug === slug)
  const t = useT()
  const { lang } = useLanguage()
  const pick = useFieldLang()

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <p>{t(STRINGS.common.loading)}</p>
      </div>
    )
  }

  if (!project) {
    return (
      <div className="error-screen">
        <p>{t(STRINGS.common.notFound)}</p>
        <button onClick={() => navigate('/ausstellungen')}>
          {t(STRINGS.common.backToExhibitions)}
        </button>
      </div>
    )
  }

  const title = pick(project.title, project.title_en) || project.title
  const description = pick(project.description, project.description_en) || project.description
  const isFallback = lang === 'en' && !project.description_en

  return (
    <article className="content-page">
      <Seo
        type="article"
        title={title}
        description={description
          .replace(/^#+\s.*$/gm, '')
          .replace(/[*_>`]/g, '')
          .replace(/\s+/g, ' ')
          .trim()
          .slice(0, 200)}
        image={project.images[0]?.url}
      />
      <div className="content-container content-container-prose">
        <button className="back-button" onClick={() => navigate('/ausstellungen')}>
          ← {t(STRINGS.common.backToExhibitions)}
        </button>

        <header className="content-header content-header-prose">
          <h1>{title}</h1>
        </header>

        {isFallback && (
          <p className="content-lang-notice">{t(STRINGS.common.contentGermanOnly)}</p>
        )}

        <Markdown className="content-prose">{description}</Markdown>

        {project.images.length > 0 && (
          <div className="project-image-grid">
            {project.images.map((img) => {
              const url = mediaUrl(img.url)
              if (!url) return null
              return (
                <a
                  key={url}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="project-image"
                >
                  <img src={url} alt={img.alt} loading="lazy" />
                </a>
              )
            })}
          </div>
        )}
      </div>
    </article>
  )
}
